/* Cart Drawer Custom — Aiku Superfoods
   Web component: handles open/close, quantity, upsell swap, cross-sell add,
   progress bar, and section rendering refreshes via Shopify Cart API. */

if (!customElements.get('cart-drawer-custom')) {
  class CartDrawerCustom extends HTMLElement {
    constructor() {
      super();
      this._busy = false;
      this._qtyTimers = {};
    }

    connectedCallback() {
      this.overlay = this.querySelector('.cdc-overlay');
      this.drawer = this.querySelector('.cdc-drawer');

      const toggle = document.getElementById('cart-drawer-toggle');
      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }

      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      document.addEventListener('cart:item-added', () => {
        this.refresh().then(() => this.open());
      });

      document.addEventListener('cart:refresh', () => {
        this.refresh();
      });

      this.drawer?.addEventListener('click', (e) => this.handleClick(e));

      this.updateProgressBar();
      this.enforceGiftRules();
    }

    /* ── Event delegation ── */

    handleClick(e) {
      const closeBtn = e.target.closest('.cdc-close');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      const minus = e.target.closest('[data-cdc-minus]');
      if (minus) {
        e.preventDefault();
        const line = parseInt(minus.dataset.cdcLine, 10);
        const item = this.querySelector(`[data-cdc-line-item="${line}"]`);
        const curQty = parseInt(item?.querySelector('.cdc-qty-val')?.textContent || '1', 10);
        this.updateQuantity(line, Math.max(0, curQty - 1));
        return;
      }

      const plus = e.target.closest('[data-cdc-plus]');
      if (plus) {
        e.preventDefault();
        const line = parseInt(plus.dataset.cdcLine, 10);
        const item = this.querySelector(`[data-cdc-line-item="${line}"]`);
        const curQty = parseInt(item?.querySelector('.cdc-qty-val')?.textContent || '1', 10);
        this.updateQuantity(line, curQty + 1);
        return;
      }

      const remove = e.target.closest('[data-cdc-remove]');
      if (remove) {
        e.preventDefault();
        const line = parseInt(remove.dataset.cdcLine, 10);
        this.updateQuantity(line, 0);
        return;
      }

      const upsell = e.target.closest('[data-cdc-upsell]');
      if (upsell) {
        e.preventDefault();
        this.handleUpsell(upsell);
        return;
      }

      const xsell = e.target.closest('[data-cdc-xsell-add]');
      if (xsell) {
        e.preventDefault();
        this.handleCrossSell(xsell);
        return;
      }

      const giftsToggle = e.target.closest('.cdc-gifts-header');
      if (giftsToggle) {
        e.preventDefault();
        giftsToggle.closest('.cdc-gifts')?.classList.toggle('is-closed');
        return;
      }

      const summaryToggle = e.target.closest('.cdc-summary-toggle');
      if (summaryToggle) {
        e.preventDefault();
        summaryToggle.closest('.cdc-summary')?.classList.toggle('is-closed');
        return;
      }
    }

    /* ── Open / Close ── */

    open() {
      if (!this.overlay || !this.drawer) return;
      this.overlay.classList.add('active');
      this.drawer.classList.add('active');
      document.body.classList.add('open-cc');
      this.drawer.focus();
      dispatchCustomEvent('cart-drawer:open');
    }

    close() {
      if (!this.overlay || !this.drawer) return;
      this.overlay.classList.remove('active');
      this.drawer.classList.remove('active');
      document.body.classList.remove('open-cc');
    }

    /* ── Quantity update (optimistic UI + debounce) ── */

    updateQuantity(line, quantity) {
      const item = this.querySelector(`[data-cdc-line-item="${line}"]`);

      if (quantity === 0) {
        clearTimeout(this._qtyTimers[line]);
        delete this._qtyTimers[line];
        if (item) {
          item.style.opacity = '.3';
          item.style.pointerEvents = 'none';
        }
        this._sendQuantity(line, 0);
        return;
      }

      const qtyVal = item?.querySelector('.cdc-qty-val');
      if (qtyVal) qtyVal.textContent = quantity;

      clearTimeout(this._qtyTimers[line]);
      this._qtyTimers[line] = setTimeout(() => {
        delete this._qtyTimers[line];
        this._sendQuantity(line, quantity);
      }, 300);
    }

    _sendQuantity(line, quantity) {
      dispatchCustomEvent('line-item:change:start', { quantity });

      fetch(theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.getSections(),
          sections_url: window.location.pathname
        })
      })
        .then((r) => r.json())
        .then((parsed) => {
          if (parsed.sections) {
            this.renderSections(parsed.sections, quantity === 0);
          }
          dispatchCustomEvent('line-item:change:end', { quantity, cart: parsed });
          if (parsed.item_count === 0) this.onCartEmpty();
        })
        .catch(console.error);
    }

    /* ── Add to cart (cross-sell / upsell add) ── */

    addToCart(variantId, quantity, sellingPlanId) {
      const body = {
        id: parseInt(variantId, 10),
        quantity: quantity || 1,
        sections: this.getSections(),
        sections_url: window.location.pathname
      };
      if (sellingPlanId) body.selling_plan = parseInt(sellingPlanId, 10);

      return fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.sections) {
            this.renderSections(response.sections);
          }
          return response;
        });
    }

    /* ── XHR helper ── */

    _xhr(url, body) {
      return new Promise((resolve, reject) => {
        const x = new XMLHttpRequest();
        x.open('POST', url, true);
        x.setRequestHeader('Content-Type', 'application/json');
        x.setRequestHeader('Accept', 'application/json');
        x.onload = () => {
          if (x.status >= 200 && x.status < 300) {
            try { resolve(JSON.parse(x.responseText)); }
            catch { resolve({}); }
          } else {
            reject(new Error(url + ' ' + x.status + ': ' + x.responseText.slice(0, 200)));
          }
        };
        x.onerror = () => reject(new Error(url + ' network error'));
        x.send(JSON.stringify(body));
      });
    }

    /* ── Upsell: swap or add ── */

    handleUpsell(btn) {
      if (this._busy) return;
      this._busy = true;
      btn.classList.add('is-loading');
      btn.textContent = 'Updating...';

      const inner = this.querySelector('.cdc-drawer-inner');
      if (inner) inner.classList.add('is-swapping');

      const action = btn.dataset.cdcAction;
      const addVariant = btn.dataset.cdcAddVariant;
      const addPlan = btn.dataset.cdcAddPlan || null;
      const removeKey = btn.dataset.cdcRemoveKey;

      const clearSwap = () => {
        const el = this.querySelector('.cdc-drawer-inner');
        if (el) el.classList.remove('is-swapping');
        this._busy = false;
      };

      const addBody = { id: parseInt(addVariant, 10), quantity: 1 };
      if (addPlan) addBody.selling_plan = parseInt(addPlan, 10);

      if (isNaN(addBody.id)) {
        clearSwap();
        return;
      }

      this._xhr('/cart/add.js', addBody)
        .then(() => {
          if (action === 'swap' && removeKey) {
            return this._xhr('/cart/change.js', {
              id: removeKey,
              quantity: 0,
              sections: this.getSections(),
              sections_url: window.location.pathname
            });
          }
        })
        .then((res) => {
          if (res && res.sections) {
            this.renderSections(res.sections);
            clearSwap();
          } else {
            this.refresh().then(clearSwap).catch(clearSwap);
          }
        })
        .catch(() => {
          this.refresh().then(clearSwap).catch(clearSwap);
        });
    }

    /* ── Cross-sell add ── */

    handleCrossSell(btn) {
      if (btn.classList.contains('is-added') || this._busy) return;
      this._busy = true;

      const variantId = btn.dataset.cdcVariantId;
      const planId = btn.dataset.cdcPlanId || null;
      const originalText = btn.textContent;

      btn.textContent = 'Adding...';
      btn.disabled = true;

      this.addToCart(variantId, 1, planId)
        .then(() => {
          btn.textContent = 'Added!';
          btn.classList.add('is-added');
        })
        .catch(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        })
        .finally(() => { this._busy = false; });
    }

    /* ── Section rendering ── */

    getSections() {
      return ['cart-drawer-custom', 'cart-bubble'];
    }

    getSectionHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector)?.innerHTML || '';
    }

    renderSections(sections, enforce) {
      const inner = sections['cart-drawer-custom'];
      if (inner) {
        const target = this.querySelector('.cdc-drawer-inner');
        if (target) {
          target.innerHTML = this.getSectionHTML(inner, '.cdc-drawer-inner');
        }
        const fresh = new DOMParser().parseFromString(inner, 'text/html').querySelector('cart-drawer-custom');
        if (fresh) {
          const eg = fresh.getAttribute('data-cdc-expected-gifts') || '';
          this.setAttribute('data-cdc-expected-gifts', eg);
        }
      }

      const bubble = sections['cart-bubble'];
      if (bubble) {
        const toggle = document.getElementById('cart-drawer-toggle');
        if (toggle) {
          const count = toggle.querySelector('.thb-item-count');
          if (count) {
            count.innerHTML = this.getSectionHTML(bubble, '.thb-item-count');
          }
        }
      }

      this.updateProgressBar();
      if (enforce !== false) this.enforceSubRequirement();
      if (!this._enforcingGifts) this.enforceGiftRules();
    }

    refresh() {
      const sections = this.getSections().join(',');
      return fetch(`${window.location.pathname}?sections=${sections}`)
        .then((r) => r.json())
        .then((parsed) => this.renderSections(parsed))
        .catch(console.error);
    }

    /* ── Subscription enforcement ── */

    enforceSubRequirement() {
      if (this._enforcing) return;
      this._enforcing = true;

      const soloIds = (this.dataset.cdcSoloVariants || '')
        .split(',').map(s => s.trim()).filter(Boolean);

      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const hasSub = cart.items.some(i =>
            i.selling_plan_allocation && i.final_price > 0
          );
          if (hasSub) return;

          const hasSoloVariant = cart.items.some(i =>
            i.final_price > 0 && soloIds.includes(String(i.variant_id))
          );
          if (hasSoloVariant) return;

          const orphaned = cart.items.filter(i =>
            i.final_price > 0 &&
            !i.selling_plan_allocation &&
            !soloIds.includes(String(i.variant_id))
          );
          if (orphaned.length === 0) return;

          const updates = {};
          orphaned.forEach(i => { updates[i.variant_id] = 0; });

          return this._xhr('/cart/update.js', { updates });
        })
        .then((res) => {
          if (res) this.refresh();
        })
        .catch(console.error)
        .finally(() => { this._enforcing = false; });
    }

    /* ── Gift rule enforcement ── */

    enforceGiftRules() {
      if (this._enforcingGifts) return;

      const raw = (this.dataset.cdcExpectedGifts || '').trim();
      const expected = raw ? raw.split(',').map(s => parseInt(s.trim(), 10)).filter(Boolean) : [];

      if (expected.length === 0) {
        this._removeOrphanGifts();
        return;
      }

      this._enforcingGifts = true;

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          const allVids = cart.items.map(i => i.variant_id);
          const autoGifts = cart.items.filter(i =>
            i.properties && i.properties._gift === 'auto'
          );

          const toAdd = expected.filter(vid => !allVids.includes(vid));
          const toRemove = autoGifts.filter(i => !expected.includes(i.variant_id));

          if (toAdd.length === 0 && toRemove.length === 0) return;

          let p = Promise.resolve();

          for (const item of toRemove) {
            p = p.then(() => this._xhr('/cart/change.js', { id: item.key, quantity: 0 }));
          }

          if (toAdd.length > 0) {
            p = p.then(() => this._xhr('/cart/add.js', {
              items: toAdd.map(vid => ({
                id: vid,
                quantity: 1,
                properties: { _gift: 'auto' }
              }))
            }));
          }

          return p;
        })
        .then(res => {
          if (res) this.refresh();
        })
        .catch(console.error)
        .finally(() => { this._enforcingGifts = false; });
    }

    _removeOrphanGifts() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          const orphans = cart.items.filter(i =>
            i.properties && i.properties._gift === 'auto'
          );
          if (orphans.length === 0) return;

          this._enforcingGifts = true;
          let p = Promise.resolve();
          for (const item of orphans) {
            p = p.then(() => this._xhr('/cart/change.js', { id: item.key, quantity: 0 }));
          }
          return p;
        })
        .then(res => {
          if (res) this.refresh();
        })
        .catch(console.error)
        .finally(() => { this._enforcingGifts = false; });
    }

    /* ── Progress bar ── */

    updateProgressBar() {
      const totalEl = this.querySelector('[data-cdc-cart-total]');
      if (!totalEl) return;

      const total = parseInt(totalEl.dataset.cdcCartTotal || '0', 10);
      const t1 = parseInt(this.dataset.cdcTier1 || '0', 10);
      const t2 = parseInt(this.dataset.cdcTier2 || '0', 10);
      const t3 = parseInt(this.dataset.cdcTier3 || '0', 10);

      const tiers = [t1, t2, t3].filter(t => t > 0);
      const maxTier = tiers.length > 0 ? tiers[tiers.length - 1] : 0;

      const fill = this.querySelector('.cdc-track-fill');
      const msg = this.querySelector('.cdc-progress-msg');
      const labels = this.querySelectorAll('.cdc-tier-label');

      if (!fill || !msg) return;

      const pct = maxTier > 0 ? Math.min(100, (total / maxTier) * 100) : 0;
      fill.style.width = pct + '%';

      const checkSvg = '<svg class="cdc-tier-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';
      const setHit = (label, isHit) => {
        if (!label) return;
        label.classList.toggle('hit', isHit);
        const has = label.querySelector('.cdc-tier-check');
        if (isHit && !has) label.insertAdjacentHTML('afterbegin', checkSvg);
        else if (!isHit && has) has.remove();
      };
      setHit(labels[0], t1 > 0 && total >= t1);
      setHit(labels[1], t2 > 0 && total >= t2);
      setHit(labels[2], t3 > 0 && total >= t3);

      const money = (c) => '$' + (c / 100).toFixed(2);
      const l1 = labels[0]?.textContent?.trim() || '';
      const l2 = labels[1]?.textContent?.trim() || '';
      const l3 = labels[2]?.textContent?.trim() || '';

      if (total >= maxTier && maxTier > 0) {
        msg.innerHTML = 'All rewards <b>unlocked!</b>';
      } else if (t3 > 0 && total >= t2 && t2 > 0) {
        msg.innerHTML = 'Add <span class="cdc-amount">' + money(t3 - total) + '</span> for <b>' + l3 + '</b>';
      } else if (t2 > 0 && total >= t1 && t1 > 0) {
        msg.innerHTML = '<b>' + l1 + ' unlocked!</b> Add <span class="cdc-amount">' + money(t2 - total) + '</span> for <b>' + l2 + '</b>';
      } else if (t1 > 0) {
        msg.innerHTML = 'Add <span class="cdc-amount">' + money(t1 - total) + '</span> to unlock <b>' + l1 + '</b>';
      }
    }

    onCartEmpty() {
      // Drawer stays open showing the empty state rendered by Liquid
    }
  }

  customElements.define('cart-drawer-custom', CartDrawerCustom);
}
